import React, { useMemo, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react";
import "swiper/swiper-bundle.min.css";
import useRefArray from "../../../hooks/useRefArray";
import { gtag } from "../../../utils/track";
import { trackZostelEvent } from "../../../utils/tracking";
import { VideoModal } from "../ImageGallery/ImageGallery";
import ImageSlideshow from "../ImageSlideshow";

interface ImageMosaicShowProps {
  images: any[];
  visibleCount: 1 | 2 | 3 | 4 | number;
  setGalleryVisible?: () => void;
  customGridClasses?: string;
  customButton?: React.ReactNode;
  onImageClick?: (index: number) => void;
  transitionDuration?: number;
  hideViewAllButton?: boolean;
  useSwiper?: boolean;
}

type ImageStructure = {
  [key: number]: {
    gridClasses: string;
    images: Array<{
      colSpan: string;
      rowSpan: string;
    }>;
  };
};

const IMAGE_STRUCTURE: ImageStructure = {
  1: {
    gridClasses: "grid-cols-1 grid-rows-1",
    images: [
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
    ],
  },
  2: {
    gridClasses: "grid-cols-2 grid-rows-1",
    images: [
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
    ],
  },
  3: {
    gridClasses: "grid-cols-2 grid-rows-2",
    images: [
      {
        colSpan: "col-span-2",
        rowSpan: "row-span-1",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
    ],
  },
  4: {
    gridClasses: "grid-cols-4 grid-rows-3",
    images: [
      {
        colSpan: "col-span-3",
        rowSpan: "row-span-3",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
    ],
  },
  5: {
    gridClasses: "grid-cols-4 grid-rows-2",
    images: [
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-2",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-2",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-2",
      },
      {
        colSpan: "col-span-1",
        rowSpan: "row-span-1",
      },
    ],
  },
};

const ImageMosaicShow: React.FC<ImageMosaicShowProps> = ({
  images,
  visibleCount,
  setGalleryVisible,
  customGridClasses,
  customButton,
  onImageClick,
  transitionDuration = 1000,
  hideViewAllButton,
  useSwiper = false,
}) => {
  const [slideshowVisible, setSlideshowVisible] = useState<boolean>(false);
  const [videoModalVisible, setVideoModalVisible] = useState<boolean>(false);
  const selectedItem = useRef<number>(0);
  const [clickedIndex, setClickedIndex] = useState<number>(0);
  const refs = useRefArray(visibleCount);

  const handleClickView = () => {
    setGalleryVisible?.();
  };

  const handleClose = () => {
    gtag("event", "ImageCloseIcon_Click");
    trackZostelEvent("ImageCloseIcon_Click");
    setSlideshowVisible(false);
    setClickedIndex(0);
  };

  const handleMediaClick = (index: number) => {
    const clickedMedia = images[index];
    if (clickedMedia.category === "video") {
      setVideoModalVisible(true);
      setClickedIndex(index);
    } else {
      setClickedIndex(index);
      setSlideshowVisible(true);
    }
    onImageClick?.(index);
  };

  const imageStructure = useMemo(
    () => IMAGE_STRUCTURE[visibleCount],
    [visibleCount]
  );
  const getVisibleImages = () => {
    return imageStructure.images.map((_imageStructure, i) => {
      if (!images[i]) {
        return (
          <div
            key={i}
            className={`w-full h-full  ${_imageStructure.rowSpan} ${_imageStructure.colSpan} bg-accent`}
          />
        );
      }

      const isVideo = images[i].category === "video";
      const media = images[i].url || images[i].image;

      return media ? (
        <CSSTransition
          classNames="fade-expand"
          timeout={transitionDuration}
          appear
          mountOnEnter
          key={i}
          nodeRef={refs.current[i]}
        >
          <div
            ref={refs.current[i]}
            className={`w-full h-full ${_imageStructure.rowSpan} ${_imageStructure.colSpan} hover:opacity-75 cursor-zoom-in relative`}
            onClick={() => handleMediaClick(i)}
            role="button"
            tabIndex={0}
            aria-label={`View ${isVideo ? "video" : "image"} ${i + 1}`}
            onKeyDown={(e) => e.key === "Enter" && handleMediaClick(i)}
          >
            {isVideo ? (
              <>
                <video
                  src={`${media}`}
                  className="object-cover md:object-cover w-full h-full"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label={`Video`}
                />
              </>
            ) : (
              <img
                src={`${media}?w=1080`}
                alt={images[i].metadata?.alt || ""}
                className="object-cover object-center w-full h-full"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = "path/to/fallback/image.jpg";
                }}
              />
            )}
          </div>
        </CSSTransition>
      ) : (
        <div
          key={i}
          className={`w-full h-full ${_imageStructure.rowSpan} ${_imageStructure.colSpan} hover:opacity-75 cursor-zoom-in bg-accent`}
        />
      );
    });
  };

  const handleVideoModalClose = () => {
    setVideoModalVisible(false);
    setClickedIndex(0);
  };

  const renderMedia = (media: string, index: number, isVideo: boolean) => (
    <div
      className="w-full rounded-2xl h-full relative"
      onClick={() => handleMediaClick(index)}
      role="button"
      tabIndex={0}
      aria-label={`View ${isVideo ? "video" : "image"} ${index + 1}`}
      onKeyDown={(e) => e.key === "Enter" && handleMediaClick(index)}
    >
      {isVideo ? (
        <video
          src={`${media}`}
          className="object-cover rounded-2xl md:object-cover w-full h-full"
          autoPlay
          muted
          loop
          playsInline
          aria-label="Video"
        />
      ) : (
        <img
          src={`${media}?w=1080`}
          alt={images[index].metadata?.alt || ""}
          className="object-cover rounded-2xl object-center w-full h-full"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = "path/to/fallback/image.jpg";
          }}
        />
      )}
    </div>
  );

  const getMobileSwiper = () => (
    <Swiper
      spaceBetween={8}
      slidesPerView="auto"
      className="w-screen bg-white"
      style={{
        marginLeft: "calc(-50vw + 50%)",
        marginRight: "calc(-50vw + 50%)",
        width: "100vw",
        paddingRight: "24px",
        paddingLeft: "24px",
      }}
    >
      {images.slice(0, visibleCount).map((image, index) => {
        const media = image.url || image.image;
        const isVideo = image.category === "video";

        return media ? (
          <SwiperSlide
            key={index}
            style={{
              width: "85%",
              height: "390px",
            }}
          >
            {renderMedia(media, index, isVideo)}
          </SwiperSlide>
        ) : null;
      })}
    </Swiper>
  );

  return (
    <>
      <section className="relative w-full h-full md:rounded-2xl">
        {useSwiper ? (
          <div className="md:hidden">{getMobileSwiper()}</div>
        ) : null}
        {useSwiper}
        <TransitionGroup
          className={`${useSwiper ? "hidden md:block" : ""} grid ${
            customGridClasses || imageStructure.gridClasses
          } gap-1 md:rounded-2xl overflow-hidden w-full h-full`}
        >
          {getVisibleImages()}
        </TransitionGroup>
        {!hideViewAllButton &&
          (customButton ? (
            customButton
          ) : (
            <button
              className="absolute bottom-0 right-0 flex items-center px-4 py-2 m-4 text-xs font-medium rounded-full bg-white"
              onClick={handleClickView}
              aria-label="View all images"
            >
              <i className="mr-2 material-icons">image</i> See All
            </button>
          ))}
      </section>
      {slideshowVisible && (
        <ImageSlideshow
          close={handleClose}
          images={images}
          carouselProps={{
            showIndicators: false,
            selectedItem: selectedItem.current,
          }}
        />
      )}
      {!videoModalVisible && slideshowVisible && (
        <ImageSlideshow
          close={handleClose}
          selectedImageId={images?.[clickedIndex]?.id}
          images={images}
        />
      )}
      {videoModalVisible && (
        <VideoModal
          videoSrc={images[clickedIndex].url || images[clickedIndex].image}
          onClose={handleVideoModalClose}
        />
      )}
    </>
  );
};

export default ImageMosaicShow;
